import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sun, Layers, Sparkles, PaintBucket, Ruler } from "lucide-react";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";

const CAT_META = {
  "Surface Protection": { icon: ShieldCheck, blurb: "Films & nano-coatings for sofas, leather, stone, wood, glass and more." },
  "Window Films": { icon: Sun, blurb: "Solar, UV, heat-rejection and privacy films for residential & commercial glass." },
  "Glass Safety": { icon: Layers, blurb: "Safety film designed to improve post-breakage glass retention." },
  "Glass Design & Transformation": { icon: Sparkles, blurb: "Transform your glass without replacing it — frosted, designer, privacy & bespoke." },
  "Home & Object Vinyl Transformation": { icon: PaintBucket, blurb: "Reversible vinyl colour & finish change for appliances, furniture and objects." },
};

export default function Services() {
  const [groups, setGroups] = useState(null);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const { lang } = useI18n();

  useEffect(() => {
    track("page_view", { page: "services" });
    api.get("/catalogue/categories").then(({ data }) => {
      setGroups(data);
      if (data[0]) setActive(data[0].category);
    }).catch(() => setGroups([]));
  }, []);

  const goQuote = (svc) => {
    track("service_quote_click", { service: svc.service_id });
    navigate("/quote", { state: { preferred_service: svc.service_name, recommended_service: svc.service_name,
      service_id: svc.service_id, category: svc.category, svc, source: "Services" } });
  };

  const current = groups?.find((g) => g.category === active);

  return (
    <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16" data-testid="services-page">
      <Seo title="Services — Surface, Glass, Window & Vinyl" description="Explore ERRĀH surface protection, window films, glass safety, glass design and home & object vinyl transformation." />

      <p className="errah-label gold-text mb-4">{lang === "hi" ? "हमारी सेवाएँ" : "Our services"}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-4xl">
        {lang === "hi" ? "हर सतह के लिए प्रीमियम सुरक्षा और ट्रांसफ़ॉर्मेशन।" : "Premium protection & transformation for every surface."}
      </h1>
      <p className="mt-5 text-gray-400 max-w-2xl text-base">
        {lang === "hi"
          ? "अपनी ज़रूरत की श्रेणी चुनें। हर सेवा प्रमाणित एप्लिकेटर द्वारा इंस्टॉल की जाती है।"
          : "Choose a category to explore. Every service is installed by a certified ERRĀH applicator."}
      </p>

      {/* Category tabs */}
      <div className="mt-10 flex flex-wrap gap-2.5" data-testid="services-category-tabs">
        {(groups || []).map((g) => {
          const Icon = CAT_META[g.category]?.icon || ShieldCheck;
          const on = g.category === active;
          return (
            <button key={g.category} onClick={() => setActive(g.category)}
              data-testid={`cat-tab-${g.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-[13px] tracking-wide rounded-sm border transition-all ${on ? "bg-[#C9A45C] text-black border-[#C9A45C]" : "border-white/15 text-gray-300 hover:border-[#C9A45C]/60"}`}>
              <Icon size={15} /> {g.category} <span className={`text-[11px] ${on ? "text-black/60" : "text-gray-500"}`}>{g.count}</span>
            </button>
          );
        })}
      </div>

      {current && (
        <div className="mt-8" data-testid="services-category-panel">
          <div className="hair-border bg-white/[0.02] p-5 mb-8">
            <p className="text-sm text-gray-300">{CAT_META[current.category]?.blurb}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {current.services.map((svc) => (
              <article key={svc.service_id} data-testid={`service-card-${svc.service_id}`}
                className="group hair-border bg-white/[0.02] overflow-hidden flex flex-col hover:border-[#C9A45C]/50 transition-colors">
                <div className="h-40 overflow-hidden bg-black/40">
                  {svc.image && <img src={svc.image} alt={svc.service_name} loading="lazy"
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-lg leading-snug">{lang === "hi" && svc.hindi_name ? svc.hindi_name : svc.service_name}</h3>
                  <p className="mt-2 text-sm text-gray-400 line-clamp-3 flex-1">{svc.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-[13px] gold-text">
                      {svc.quote_required ? <><Ruler size={13} /> {lang === "hi" ? "मेज़रमेंट पर कोट" : "Quote on measurement"}</>
                        : <>{lang === "hi" ? "शुरू" : "From"} ₹{svc.base_rate}/{svc.pricing_unit}</>}
                    </span>
                  </div>
                  <button onClick={() => goQuote(svc)} data-testid={`service-quote-${svc.service_id}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black py-3 text-[13px] font-semibold hover:bg-[#E4C987] transition-colors">
                    {svc.quote_required ? (lang === "hi" ? "मेज़रमेंट और कोट लें" : "Get measurement & quote") : (lang === "hi" ? "कोट लें" : "Get a quote")}
                    <ArrowRight size={15} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {groups && groups.length === 0 && (
        <p className="mt-10 text-gray-500" data-testid="services-empty">No services available right now.</p>
      )}
    </div>
  );
}
